<script setup lang="ts">
import { useNavigation } from "../../../composables/useNavigation";
import type { Language, NavItem } from "../../../types/navbar";
import useTracking from "@citizenm/lobby/composables/useTracking";

defineProps<{
  navItems: NavItem[];
  languages: Language[];
  auth: {
    firstName: string;
    logout: () => void;
    loginWithRedirect: (args: { appState: { target: string } }) => void;
    isAuthenticated: boolean;
  };
}>();

const { trackMainNavi } = useTracking();
const localePath = useLocalePath();
const { t } = useI18n();
const { y: yScrollPosition } = useScroll(document);
const route = useRoute();

const isScrolled = computed(() => yScrollPosition.value > 0);
const { navigationState, toggleSubMenu, closeAll } = useNavigation();
const navbar = useTemplateRef<HTMLElement>("navbar");

const handleClickOutside = () => {
  if (navigationState.value.mobileDrawerOpen) {
    return;
  }
  closeAll();
};

onClickOutside(navbar, handleClickOutside);
</script>

<template>
  <header
    ref="navbar"
    :class="['page-header', isScrolled && 'page-header--shadow']"
  >
    <nav class="page-header__container container">
      <NuxtLink class="page-header__logo" :to="localePath('/')">
        <SvgoLogo :font-controlled="false" filled />
        <label class="u-visually-hidden"> Link to homepage </label>
      </NuxtLink>

      <div class="navigation">
        <ul class="navigation__list">
          <li v-for="navItem in navItems" :key="navItem.title">
            <LayoutNavBarItem
              v-if="navItem.navigationItems"
              :is-active="
                navigationState.openedNavigationDropDownId === navItem.title
              "
              @click="
                () => {
                  toggleSubMenu(navItem.title);
                  trackMainNavi(navItem.title);
                }
              "
            >
              {{ navItem.title }}</LayoutNavBarItem
            >
            <LayoutNavBarItem
              v-else
              :to="
                navItem.slug
                  ? localePath(String(navItem.slug))
                  : navItem.externalLinkUrl
              "
              @click="trackMainNavi(navItem.title)"
              >{{ navItem.title }}</LayoutNavBarItem
            >
            <LayoutNavigationDropDown
              v-if="navItem.navigationItems"
              :is-open="
                navigationState.openedNavigationDropDownId === navItem.title
              "
              :navigation-items="navItem.navigationItems"
            />
          </li>
        </ul>
      </div>

      <div class="navigation navigation--right">
        <ul class="navigation__list">
          <li class="navigation__item--relative">
            <LayoutNavBarItem
              :is-active="
                navigationState.openedNavigationDropDownId === 'language'
              "
              @click="
                () => {
                  trackMainNavi('language-selection');
                  toggleSubMenu('language');
                }
              "
            >
              <span class="u-visually-hidden">
                {{ t("website.navigation.languages.title") }}
              </span>
              <SvgoGlobe class="navigation__icon" :font-controlled="false" />
            </LayoutNavBarItem>
            <LayoutLanguageDropDown
              v-if="navigationState.openedNavigationDropDownId === 'language'"
              :languages="languages"
            />
          </li>
          <li>
            <LayoutNavBarItem
              :to="localePath('/get-booking')"
              @click="trackMainNavi(t('website.navigation.booking.title'))"
            >
              {{ t("website.navigation.booking.title") }}
            </LayoutNavBarItem>
          </li>
          <li class="navigation__item--relative">
            <div class="navigation__divider" />
            <template v-if="auth.isAuthenticated">
              <FoundationLoadingSpinner
                v-if="!auth.firstName"
                class="navigation__item__loading"
              />
              <LayoutNavBarItem
                v-else
                data-testid="logged-in-menu-button"
                :is-active="
                  navigationState.openedNavigationDropDownId === 'login'
                "
                @click="
                  () => {
                    trackMainNavi(t('website.navigation.login.title'));
                    toggleSubMenu('login');
                  }
                "
              >
                Hi, {{ auth.firstName }}
              </LayoutNavBarItem>
              <LayoutDropdown
                v-if="navigationState.openedNavigationDropDownId === 'login'"
              >
                <LayoutLoggedInMenu
                  data-testid="logged-in-menu"
                  @logout="auth.logout"
                />
              </LayoutDropdown>
            </template>

            <LayoutNavBarItem
              v-else
              data-testid="log-in-button"
              @click="
                () => {
                  trackMainNavi('login');
                  auth.loginWithRedirect({
                    appState: {
                      target: route.fullPath,
                    },
                  });
                }
              "
            >
              {{ t("website.navigation.login.title") }}
            </LayoutNavBarItem>
          </li>
        </ul>
      </div>
      <LayoutNavToggle class="navigation__burger" />
    </nav>
  </header>

  <div v-if="navigationState.mobileDrawerOpen" id="mobile-drawer">
    <LayoutMobileDrawer :nav-items="navItems" :languages :auth />
  </div>
</template>

<style scoped lang="scss">
@use "@citizenm/lobby/assets/styles/utils/mixins" as mixins;

.page-header {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  width: 100%;
  height: var(--header-navigation-height-mobile, 60px);
  padding: 0;
  background-color: var(--primary-white, #fff);
  transition: 0.3s;

  &--shadow {
    box-shadow: var(--shadow, 0 2px 25px 0 rgb(0 0 0 / 6%));
  }

  @include mixins.lg {
    height: var(--header-navigation-height, 80px);
  }

  &__logo {
    display: block;
    font-size: 3rem;

    svg {
      width: 1em;
      height: 1em;
    }

    @include mixins.lg {
      font-size: 3.5rem;
    }
  }

  &__container {
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    box-sizing: border-box;
  }

  .relative {
    position: relative;
  }
}

.navigation {
  display: none;
  gap: var(--spacing-8, 2rem);
  align-items: flex-end;
  margin-left: var(--spacing-12, 3rem);

  @include mixins.lg {
    display: flex;
  }

  &--right {
    margin-left: auto;

    .nav-bar-item {
      font-weight: normal;
    }
  }

  &__list {
    display: flex;
    gap: var(--spacing-8, 2rem);
    margin: 0;
    padding: 0;
    list-style-type: none;
  }

  &__icon {
    flex: 0 0 18px;
    aspect-ratio: 1;
  }

  &__item {
    &--relative {
      position: relative;
    }

    &__loading {
      display: block;
      margin-top: 32px;
    }
  }

  &__divider {
    position: absolute;
    top: 2rem;
    left: -1rem;
    width: 1px;
    height: 20px;
    background-color: var(--color-light-grey-surface, #f1f1f1);
  }

  &__burger {
    margin-left: auto;
    padding: 0;

    @include mixins.lg {
      display: none;
    }
  }
}
</style>
